import { Collapse, Divider } from 'antd';
import classNames from 'classnames';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useGetCurrencySymbol } from 'app/appState';
import { User, getUserName, useRole } from 'entities/User';
import { ReactComponent as CollapseArrow } from 'shared/assets/icons/CollapseArrow.svg';
import { BulletsTable, TableRow } from 'shared/ui/BulletsTable';
import { Button } from 'shared/ui/Button';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { Payment, PaymentStatus, generatePaymentPdfLink } from 'entities/Payment';
import { MarkAsRefundedPayment } from 'features/MarkAsRefundedPayment';
import { CreditAndRefundPayment } from 'features/CreditAndRefundPayment';
import { CreditPayment } from 'features/CreditPayment';
import s from './PaymentCard.module.scss';
import { Deposit } from 'entities/Deposit';
import { splitDocumentDescription } from 'shared/utils/helpers/splitDocumentDescription';
import { ReceiptActions } from 'features/ReceiptActions';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

interface PaymentCardProps {
  user: User;
  payment: Payment;
  deposit?: Deposit;
  isClosedContract: boolean;
  isDepositPayment?: boolean;
}

export const PaymentCard: FC<PaymentCardProps> = memo((props) => {
  const { payment, user, isDepositPayment, deposit, isClosedContract } = props;

  const { documentNumber: invoiceNumber, invoiceEmail } = payment.invoice;
  const { t } = useAppTranslation(['contracts', 'common', 'payment']);
  const { isManagement } = useRole();
  const currencySymbol = useGetCurrencySymbol();

  const [isCollapseOpen, setCollapseOpen] = useState(false);

  const showIsDepositPayment = useMemo(() => Boolean(isDepositPayment && deposit && !deposit.isRefunded), [deposit, isDepositPayment]);

  const isPaymentCompleted = payment.paymentStatus === PaymentStatus.COMPLETED;
  const creditedAmount = payment.creditNote?.creditedAmount;
  const refundedAmount = payment.refund?.amount;

  const toggleCollapse = useCallback(() => {
    setCollapseOpen((prev) => !prev);
  }, []);

  const activePaymentAmount = useMemo(() => {
    const amount = payment.creditNote ? payment.amount - payment.creditNote.creditedAmount : payment.amount;
    return roundNumber(amount);
  }, [payment.amount, payment.creditNote]);

  const PaymentContent = useCallback((): JSX.Element => {
    const mainRows: TableRow[] = [
      {
        label: t('Payment method'),
        value: t(payment.paymentMethod, { ns: 'common' }),
      },
      {
        label: t('Payment'),
        value: `${formatPrice(roundNumber(payment.amount))} ${currencySymbol}`,
        emptyValueSymbol: `0 ${currencySymbol}`,
      },
    ];

    if (isManagement) {
      const splitedDescription = splitDocumentDescription(payment.description);

      mainRows.push({
        label: t('Description'),
        value: `${t(splitedDescription.description, { ns: 'common' })} ${splitedDescription.documentNumber}`,
        emptyValueSymbol: '-',
      });

      if (payment.markedAsPaidBy) {
        mainRows.push({
          label: t('Marked as paid by'),
          value: getUserName(payment.markedAsPaidBy) || null,
          emptyValueSymbol: '-',
        });
      }
    }

    const creditNoteRows: TableRow[] = [];
    if (payment.creditNote) {
      creditNoteRows.push({
        label: t('Cancelled amount'),
        value: `-${formatPrice(roundNumber(payment.creditNote.creditedAmount))} ${currencySymbol}`,
        emptyValueSymbol: `0 ${currencySymbol}`,
      });
      creditNoteRows.push({
        label: t('Date'),
        value: `${getClientDateFormat(payment.creditNote.createdAt)}`,
        emptyValueSymbol: '-',
      });

      if (isManagement) {
        const splitedDescription = splitDocumentDescription(payment.creditNote.description);

        creditNoteRows.push({
          label: t('Description'),
          value: `${t(splitedDescription.description, { ns: 'common' })} ${splitedDescription.documentNumber}`,
          emptyValueSymbol: '-',
        });
        creditNoteRows.push({
          label: t('Cancelled by'),
          value: getUserName(payment.creditNote.issuedBy) || null,
          emptyValueSymbol: '-',
        });
      }
    }

    const refundRows: TableRow[] = [];
    if (payment.refund) {
      refundRows.push({
        label: t('Refund method'),
        value: t(payment.refund.paymentMethod || '-', { ns: 'common' }),
      });
      refundRows.push({
        label: t('Refund amount'),
        value: `${formatPrice(roundNumber(payment.refund.amount))} ${currencySymbol}`,
        emptyValueSymbol: `0 ${currencySymbol}`,
      });
      refundRows.push({
        label: t('Date'),
        value: `${getClientDateFormat(payment.refund.createdAt)}`,
        emptyValueSymbol: '-',
      });

      if (isManagement) {
        if (payment.refund.description) {
          const splitedDescription = splitDocumentDescription(payment.refund.description);

          refundRows.push({
            label: t('Description'),
            value: `${t(splitedDescription.description, { ns: 'common' })} ${splitedDescription.documentNumber}`,
            emptyValueSymbol: '-',
          });
        }
        refundRows.push({
          label: t('Refunded by'),
          value: getUserName(payment.refundedBy) || null,
          emptyValueSymbol: '-',
        });
      }
    }

    return (
      <div>
        <div className="text-lg font-semibold mb-4 font-tektur">{t('Customer details')}</div>
        <div className="flex flex-col">
          <span className="text-sm text-grey">{t('From')}</span>
          <span className="text-primaryLight font-tektur">{getUserName(user)}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-sm text-grey">{t('Mail')}</span>
          <span className="text-primaryLight font-tektur">{invoiceEmail}</span>
        </div>
        <Divider className="border-primary my-4" />

        <BulletsTable theme="clear" rows={mainRows} />

        {creditNoteRows.length > 0 && (
          <>
            <Divider className="border-primary my-4" />
            <BulletsTable theme="clear" rows={creditNoteRows} />
          </>
        )}

        {refundRows.length > 0 && (
          <>
            <Divider className="border-primary my-4" />
            <BulletsTable theme="clear" rows={refundRows} />
          </>
        )}

        <Divider className="border-primary my-4" />
        <BulletsTable
          theme="clear"
          rows={[
            {
              label: t('Total'),
              value: `${formatPrice(activePaymentAmount)} ${currencySymbol}`,
              emptyValueSymbol: `0 ${currencySymbol}`,
              highlighted: true,
            },
          ]}
        />

        {isManagement && !payment.refund && (
          <div className="flex flex-col gap-3 mt-6">
            {(isClosedContract || payment.creditNote) && (
              <MarkAsRefundedPayment
                payment={payment}
                showIsDepositPayment={showIsDepositPayment}
                isClosedContract={isClosedContract}
                deposit={deposit}
                action={<Button theme="secondary">{t('Mark as Refunded')}</Button>}
              />
            )}
            {!payment.creditNote && (
              <CreditPayment
                paymentId={payment.paymentId}
                amount={payment.amount}
                action={<Button theme="danger">{t('Cancel Payment')}</Button>}
              />
            )}
            {(isClosedContract || payment.creditNote) && (
              <CreditAndRefundPayment
                payment={payment}
                isClosedContract={isClosedContract}
                showIsDepositPayment={showIsDepositPayment}
                deposit={deposit}
                action={<Button theme="dark">{t('Refund')}</Button>}
              />
            )}
          </div>
        )}

        {!payment.creditNote && (
          <div className="flex flex-col gap-2 mt-2 py-4 bg-white">
            <a
              href={generatePaymentPdfLink(payment.paymentId)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-accent"
            >
              <Button containerClassName="w-full" theme="outlined" className="!text-grey !border-grey">
                {t('Download PDF')}
              </Button>
            </a>
          </div>
        )}

        <ReceiptActions paymentId={payment.paymentId} isReceiptIssued={payment.isReceiptIssued} />

        <div className="mt-2 cursor-pointer w-full flex justify-center" onClick={toggleCollapse}>
          <CollapseArrow
            className={classNames('fill-primary w-6 h-6 cursor-pointer transition-transform duration-700 ease-in-out', {
              '-rotate-90': isCollapseOpen,
              '!hidden': !isCollapseOpen,
            })}
          />
        </div>
      </div>
    );
  }, [
    t,
    payment,
    currencySymbol,
    isManagement,
    user,
    invoiceEmail,
    activePaymentAmount,
    isClosedContract,
    showIsDepositPayment,
    deposit,
    toggleCollapse,
    isCollapseOpen,
  ]);

  const isCreditedPayment = creditedAmount && creditedAmount === payment.amount;
  const isCompletedPayment = isPaymentCompleted && !payment.refund && !payment.creditNote;
  const isRefundedPayment = refundedAmount && refundedAmount === payment.amount;
  const isPartiallyRefunded = refundedAmount && refundedAmount !== payment.amount;
  const isPartiallyCredited = creditedAmount && !refundedAmount && creditedAmount !== payment.amount;

  const getPaymentStatus = (): string => {
    if (isCompletedPayment) return 'Paid';
    if (isRefundedPayment) return 'Refunded';
    if (isPartiallyRefunded) return 'Partially refunded';
    if (isPartiallyCredited) return 'Partially cancelled';
    return 'Cancelled';
  };

  const getReceiptStatus = (): string => {
    if (payment.isReceiptIssued) {
      return t('Receipt is issued', { ns: 'payment' });
    }

    return t('Receipt is not issued', { ns: 'payment' });
  };

  return (
    <Collapse
      className={classNames(s.collapse)}
      activeKey={isCollapseOpen ? payment.paymentId : undefined}
      onChange={(keys) => {
        setCollapseOpen(!!keys.length);
      }}
      items={[
        {
          key: payment.paymentId,
          label: (
            <>
              <div className="font-semibold text-lg flex items-center justify-between py-1 items-start">
                <div className="font-semibold text-lg flex justify-between flex flex-col font-tektur">
                  {t('Payment')} {payment.documentNumber}
                  <div className="text-primaryLight">
                    {t('Invoice')} {invoiceNumber}
                  </div>
                </div>
                <div>
                  <div
                    className={classNames('flex gap-1 items-center justify-center text-sm text-center rounded-xl px-2 py-0.5', {
                      'text-success bg-successLight': isCompletedPayment,
                      'text-error bg-errorLight': isCreditedPayment || isPartiallyCredited,
                      'text-indigo-500 bg-indigo-200': isRefundedPayment || isPartiallyRefunded,
                    })}
                  >
                    {t(getPaymentStatus())}
                  </div>
                  <div
                    className={classNames('flex gap-1 justify-center items-center text-sm rounded-xl px-2 py-0.5 mt-3', {
                      'border border-accent text-primary': payment.isReceiptIssued,
                      'text-error bg-errorLight': !payment.isReceiptIssued,
                    })}
                  >
                    {t(getReceiptStatus())}
                  </div>
                </div>
              </div>
              <div className="text-sm text-primaryLight">{getClientDateFormat(payment.createdAt)}</div>
            </>
          ),
          children: <PaymentContent />,
        },
      ]}
      expandIcon={() => (
        <CollapseArrow
          onClick={toggleCollapse}
          className={classNames(
            'fill-primary absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-6 cursor-pointer transition-transform duration-700 ease-in-out',
            {
              '!hidden': isCollapseOpen,
              'rotate-90': !isCollapseOpen,
            },
          )}
        />
      )}
    />
  );
});
